/**
 * PSTUDIO workflow-table perf: the cell renderer (`cell.tsx`) only mounts a live
 * form control (`<input>/<textarea>/<select>`) for the cell that is the active
 * edit target (`tableStore.editingCellId === cellId`) or the currently
 * selected/focused cell (`tableStore.isSelectedStartCell(cellId)` — mounted so
 * the W3C-grid keyboard navigation has a focusable element to step into, see
 * PSTUDIO-3153 in `cell.tsx`); every other cell renders a lightweight `<span>`
 * of its display value. The SELECT columns gate tighter — `editing &&
 * isSelectedStartCell` — so they stay a plain `<span>` in view mode. That keeps
 * the DOM small on large workspaces (~25 columns × ~50 rendered rows previously
 * meant ~1,250 live controls mounted at rest).
 *
 * Because inactive cells no longer contain a control, the store's edit-entry
 * methods (`toggleCellInput`, `startCellEditingWithChar`) can no longer decide
 * what to do by querying the cell DOM — there is nothing to find. They branch on
 * the column's edit *kind* instead, computed here so the renderer and the store
 * agree on a single source of truth.
 */
import type { NodeColumns, TableRow } from '../table-store';

export type ColumnEditKind =
  | 'checkbox' // toggled by a single click; control stays always-mounted
  | 'select' // native <select> (nodeType/richAssetType/command) or ps-select (analyticsEvent); mounts on edit
  | 'behaviors' // design-system multi-select; control stays always-mounted
  | 'readonly' // never editable (computed/derived columns)
  | 'number' // <input type="number">; mounts on edit, type-to-edit is char-filtered
  | 'text'; // <input>/<textarea>/array — mounts on edit, supports type-to-edit

// Single source of truth for the column classification. `cell.tsx` imports
// CHECKBOX_COLUMNS / READ_ONLY_COLUMNS directly; the SELECT columns render as
// four separate branches there (nodeType / richAssetType / command /
// analyticsEvent), so a unit test (`column-edit-kind.test.ts`) asserts the sets
// stay in lockstep with the renderer instead.
export const CHECKBOX_COLUMNS = new Set<string>([
  'answerRequired',
  'nluDisabled',
]);
export const SELECT_COLUMNS = new Set<string>([
  'nodeType',
  'richAssetType',
  'command',
  // PSTUDIO-4784: a select of the standard events that also takes a typed name.
  // Being a select column, it is NOT a free-text column: double-click, Enter, F2
  // and type-to-edit no longer auto-open the Expanded Cell View for it, and it has
  // no Clip/Wrap setting (`isWrappableTextColumn` needs kind 'text'; the cell
  // shows one line). It stays JSON-capable for the panel's editor
  // (`is-json-capable-column.ts`), the one place to edit more than `event_name`.
  'analyticsEvent',
]);
export const READ_ONLY_COLUMNS = new Set<string>(['order', 'id']);
const NUMBER_COLUMNS = new Set<string>(['nodeNumber']);

export function getColumnEditKind(
  columnId: keyof TableRow | NodeColumns
): ColumnEditKind {
  if (CHECKBOX_COLUMNS.has(columnId)) return 'checkbox';
  if (columnId === 'behaviors') return 'behaviors';
  if (SELECT_COLUMNS.has(columnId)) return 'select';
  if (READ_ONLY_COLUMNS.has(columnId)) return 'readonly';
  if (NUMBER_COLUMNS.has(columnId)) return 'number';
  // Every remaining known column edits as free text (input/textarea/array
  // input). This is also the deliberate fallback for column ids this module
  // doesn't know about — text is the safe majority case.
  return 'text';
}

// Columns that fall through getColumnEditKind as 'text' but are not grid
// text the user wraps: the embed header, the hidden settings blob, and the
// version id.
const NOT_WRAPPABLE_TEXT = new Set(['embed', 'settings', 'solutionVersionId']);

/** Free-text grid columns that offer Clip/Wrap and edit-time wrapping. */
export function isWrappableTextColumn(columnId: string): boolean {
  if (NOT_WRAPPABLE_TEXT.has(columnId)) return false;
  return getColumnEditKind(columnId as NodeColumns) === 'text';
}
